/**
 * Filter Suggestions Component
 * Displays location and department filter suggestions as clickable chips
 */

import React from 'react';
import { MapPin, Building2 } from 'lucide-react';

export interface FilterSuggestionsProps {
  locationKeywords?: string[];
  departmentNames?: string[];
  locationCount?: number;
  departmentCount?: number;
  onSelectFilter: (filterType: 'location' | 'department', filterValue: string) => void;
  selectedLocations?: Set<string>;
  selectedDepartments?: Set<string>;
  maxVisible?: number;
}

export function FilterSuggestions({
  locationKeywords = [],
  departmentNames = [],
  locationCount = 0,
  departmentCount = 0,
  onSelectFilter,
  selectedLocations = new Set(),
  selectedDepartments = new Set(),
  maxVisible = 10,
}: FilterSuggestionsProps) {
  // Don't render if no filters available
  if (locationKeywords.length === 0 && departmentNames.length === 0) {
    return null;
  }

  const handleLocationClick = (location: string) => {
    onSelectFilter('location', location);
  };

  const handleDepartmentClick = (department: string) => {
    onSelectFilter('department', department);
  };

  return (
    <div className="space-y-3">
      {/* Location Filters */}
      {locationKeywords.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
            <MapPin className="h-3.5 w-3.5" />
            <span className="font-medium">
              Location Filters {locationCount > 0 && `(${locationCount})`}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {locationKeywords.slice(0, maxVisible).map((location, index) => {
              const isSelected = selectedLocations.has(location);
              return (
                <button
                  key={`location-${index}`}
                  onClick={() => handleLocationClick(location)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    isSelected
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-300 dark:border-blue-700'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
                  }`}
                  title={`Filter by location: ${location}`}
                >
                  <MapPin className="h-3 w-3" />
                  <span>{location}</span>
                </button>
              );
            })}
            {locationKeywords.length > maxVisible && (
              <span className="inline-flex items-center px-2.5 py-1 text-xs text-gray-500 dark:text-gray-400">
                +{locationKeywords.length - maxVisible} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Department Filters */}
      {departmentNames.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
            <Building2 className="h-3.5 w-3.5" />
            <span className="font-medium">
              Department Filters {departmentCount > 0 && `(${departmentCount})`}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {departmentNames.slice(0, maxVisible).map((department, index) => {
              const isSelected = selectedDepartments.has(department);
              return (
                <button
                  key={`department-${index}`}
                  onClick={() => handleDepartmentClick(department)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    isSelected
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border border-purple-300 dark:border-purple-700'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
                  }`}
                  title={`Filter by department: ${department}`}
                >
                  <Building2 className="h-3 w-3" />
                  <span>{department}</span>
                </button>
              );
            })}
            {departmentNames.length > maxVisible && (
              <span className="inline-flex items-center px-2.5 py-1 text-xs text-gray-500 dark:text-gray-400">
                +{departmentNames.length - maxVisible} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
